import React from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const QuestionCard = ({ 
  question, 
  response, 
  onResponseChange, 
  onNext, 
  onPrevious, 
  isFirst, 
  isLast, 
  isValid 
}) => {
  const handleInputChange = (value) => {
    onResponseChange(question?.id, value);
  };

  const handleMultipleChoiceChange = (optionId) => {
    if (question?.allowMultiple) {
      const currentValues = response?.value || [];
      const newValues = currentValues?.includes(optionId)
        ? currentValues?.filter(id => id !== optionId)
        : [...currentValues, optionId];
      onResponseChange(question?.id, newValues);
    } else {
      onResponseChange(question?.id, optionId);
    }
  };

  const renderQuestionInput = () => {
    switch (question?.type) {
      case 'multiple_choice':
        return (
          <div className="space-y-3">
            {question?.options?.map((option) => (
              <div key={option?.id} className="flex items-start space-x-3">
                <Checkbox
                  checked={question?.allowMultiple 
                    ? (response?.value || [])?.includes(option?.id)
                    : response?.value === option?.id
                  }
                  onChange={() => handleMultipleChoiceChange(option?.id)}
                />
                <div className="flex-1">
                  <label className="text-sm font-medium text-foreground cursor-pointer">
                    {option?.label}
                  </label>
                  {option?.description && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {option?.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        );

      case 'text':
        return (
          <Input
            type="text"
            placeholder={question?.placeholder || "Enter your response..."}
            value={response?.value || ''}
            onChange={(e) => handleInputChange(e?.target?.value)}
            className="w-full"
          />
        );

      case 'textarea':
        return (
          <textarea
            placeholder={question?.placeholder || "Enter detailed response..."}
            value={response?.value || ''}
            onChange={(e) => handleInputChange(e?.target?.value)}
            rows={4}
            className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            placeholder={question?.placeholder || "Enter number..."}
            value={response?.value || ''}
            onChange={(e) => handleInputChange(e?.target?.value)}
            min={question?.min}
            max={question?.max}
            className="w-full"
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-elevation-1">
      {/* Question Header */}
      <div className="mb-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              {question?.title}
            </h3>
            {question?.description && (
              <p className="text-sm text-muted-foreground">
                {question?.description}
              </p>
            )}
          </div>
          {question?.required && (
            <span className="text-destructive text-sm font-medium ml-2">
              Required
            </span>
          )}
        </div>

        {/* Question Metadata */}
        <div className="flex items-center space-x-4 text-xs text-muted-foreground">
          <span className="flex items-center space-x-1">
            <Icon name="Hash" size={12} />
            <span>Q{question?.sequence}</span>
          </span>
          <span className="flex items-center space-x-1">
            <Icon name="Tag" size={12} />
            <span>{question?.category}</span>
          </span>
          {question?.sapProcess && (
            <span className="flex items-center space-x-1">
              <Icon name="Settings" size={12} />
              <span>{question?.sapProcess}</span>
            </span>
          )}
        </div>
      </div>
      {/* Question Input */}
      <div className="mb-6">
        {renderQuestionInput()}
      </div>
      {/* Conditional Logic Info */}
      {question?.conditionalLogic && (
        <div className="mb-6 p-3 bg-muted rounded-md">
          <div className="flex items-start space-x-2">
            <Icon name="Info" size={16} className="text-accent mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground">
                Conditional Question
              </p>
              <p className="text-xs text-muted-foreground">
                This question appears based on your previous responses
              </p>
            </div>
          </div>
        </div>
      )}
      {/* Navigation */}
      <div className="flex items-center justify-between pt-4 border-t border-border">
        <Button
          variant="outline"
          onClick={onPrevious}
          disabled={isFirst}
          iconName="ChevronLeft"
          iconPosition="left"
        >
          Previous
        </Button>

        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            iconName="Save"
            iconPosition="left"
          >
            Save Progress
          </Button>
          
          <Button
            variant="default"
            onClick={onNext}
            disabled={question?.required && !isValid}
            iconName={isLast ? "Check" : "ChevronRight"}
            iconPosition="right"
          >
            {isLast ? 'Complete' : 'Next'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuestionCard;